import axios from "axios";
import { BACKEND_URL } from "@/lib/env";

const BASE = `${BACKEND_URL}/api/admin/users`;

/**
 * @param {unknown} err — axios error
 * @returns {string}
 */
export function formatAdminApiError(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => (typeof item === "object" && item?.msg ? item.msg : String(item)))
      .join("; ");
  }
  if (err?.response?.status === 403) {
    return "You do not have permission to manage users.";
  }
  if (err?.response?.status === 401) {
    return "Session expired. Sign in again.";
  }
  return err?.message || "Request failed";
}

/**
 * @param {{ limit?: number, nextToken?: string | null }} [opts]
 * @returns {Promise<{ users: AdminUser[], next_token: string | null }>}
 */
export async function listAdminUsers(opts = {}) {
  const params = { limit: opts.limit ?? 60 };
  if (opts.nextToken) params.next_token = opts.nextToken;
  const { data } = await axios.get(BASE, { params });
  return data;
}

/**
 * @param {{ email: string, group_slugs: string[] }} body
 * @returns {Promise<AdminUser>}
 */
export async function createAdminUser(body) {
  const { data } = await axios.post(BASE, body);
  return data;
}

/**
 * @param {string} username
 * @param {{ enabled?: boolean, group_slugs?: string[] }} patch
 * @returns {Promise<AdminUser>}
 */
export async function updateAdminUser(username, patch) {
  const { data } = await axios.patch(
    `${BASE}/${encodeURIComponent(username)}`,
    patch,
  );
  return data;
}

/**
 * @param {string} username
 * @returns {Promise<AdminUser>}
 */
export async function unlockAdminUser(username) {
  const { data } = await axios.post(
    `${BASE}/${encodeURIComponent(username)}/unlock`,
  );
  return data;
}

/** @typedef {{ username: string, email?: string | null, enabled: boolean, status?: string | null, groups: string[], failed_login_attempts?: number, account_locked?: boolean }} AdminUser */
